import { useQuery } from '@tanstack/react-query'
import { CalendarDays, MapPin, Ticket } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  SPONSOR_TIER_ORDER,
  coverImageStyle,
  formatBRL,
  formatDateTime,
  type SponsorTier,
} from '@paroquia/shared'
import { Alert, Button, Card, CardBody, CardHeader, PageLoader } from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError, apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { EventCard, EventDetail, Paginado } from '@/lib/portal-types'
import { GaleriaDeFotos } from './GaleriaDeFotos'
import { CartaoDeEvento, Vazio } from './components'

export function EventosPage() {
  const [quando, setQuando] = useState<'upcoming' | 'past'>('upcoming')
  useDocumentTitle('Eventos')

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'eventos', quando],
    queryFn: () => apiGet<Paginado<EventCard>>('/api/portal/eventos', { when: quando, perPage: 24 }),
  })

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="font-serif text-3xl font-semibold">Eventos</h1>
      <p className="mt-1 text-suave">
        Festas, retiros e encontros da Paroquia de Pacaembu.
      </p>

      <div role="group" aria-label="Periodo" className="mt-6 flex gap-2">
        {(
          [
            { valor: 'upcoming', label: 'Proximos' },
            { valor: 'past', label: 'Ja realizados' },
          ] as const
        ).map((opcao) => (
          <button
            key={opcao.valor}
            type="button"
            onClick={() => setQuando(opcao.valor)}
            aria-pressed={quando === opcao.valor}
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium',
              quando === opcao.valor
                ? 'border-primario bg-primario text-sobre-primario'
                : 'border-borda hover:bg-afundado',
            )}
          >
            {opcao.label}
          </button>
        ))}
      </div>

      {isLoading && <PageLoader />}
      {error && (
        <Alert tone="danger" className="mt-6">
          {error instanceof ApiError ? error.message : 'Nao foi possivel carregar os eventos.'}
        </Alert>
      )}

      {data && data.items.length === 0 && (
        <div className="mt-6">
          <Vazio
            titulo={quando === 'upcoming' ? 'Nenhum evento marcado' : 'Nenhum evento anterior'}
            texto="Acompanhe esta pagina para as proximas festas e encontros da paroquia."
          />
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {data.items.map((evento) => (
            <CartaoDeEvento key={evento.id} evento={evento} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Pagina publica de divulgacao do evento (RF03.1, RF03.2 e RF03.3). */
export function EventoDetalhePage() {
  const { slug } = useParams()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'evento', slug],
    queryFn: () => apiGet<EventDetail>(`/api/portal/eventos/${slug}`),
    enabled: !!slug,
  })

  useDocumentTitle(data?.title)

  if (isLoading) return <PageLoader />

  if (error) {
    const naoEncontrado = error instanceof ApiError && error.status === 404
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <h1 className="font-serif text-2xl font-semibold">
          {naoEncontrado ? 'Evento nao encontrado' : 'Nao foi possivel abrir o evento'}
        </h1>
        <Link to="/eventos" className="mt-6 inline-block font-medium underline">
          Ver todos os eventos
        </Link>
      </div>
    )
  }
  if (!data) return null

  const situacao = data.inscricoes
  /**
   * O formulario nao e mais pre-requisito: a API devolve a configuracao padrao
   * quando o evento nao montou um. Ativar inscricoes basta.
   *
   * A verificacao de `formulario` continua como rede de seguranca, mas agora
   * com explicacao visivel - antes a ausencia dela escondia o botao sem dizer
   * nada, e o fiel nao encontrava onde se inscrever.
   */
  const podeInscrever = Boolean(
    data.registrationsEnabled && situacao?.aberta && data.formulario,
  )
  const inscricaoIndisponivelSemMotivo =
    data.registrationsEnabled && situacao?.aberta && !data.formulario

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {data.cover && (
        <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-primario-suave">
          <img
            src={data.cover.large}
            alt={data.cover.alt ?? ''}
            className="h-full w-full"
            style={coverImageStyle(data.coverFit, data.coverPosition)}
          />
        </div>
      )}

      <header className="mt-6">
        <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{data.title}</h1>

        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-2">
            <CalendarDays className="mt-0.5 size-5 shrink-0 text-primario" aria-hidden="true" />
            <div>
              <dt className="text-sm text-suave">Quando</dt>
              <dd className="font-medium">
                <time dateTime={data.startsAt}>{formatDateTime(data.startsAt)}</time>
                {data.endsAt && (
                  <>
                    {' ate '}
                    <time dateTime={data.endsAt}>{formatDateTime(data.endsAt)}</time>
                  </>
                )}
              </dd>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 size-5 shrink-0 text-primario" aria-hidden="true" />
            <div>
              <dt className="text-sm text-suave">Onde</dt>
              <dd className="font-medium">
                {data.location}
                {data.address && (
                  <span className="block font-normal text-suave">
                    {data.address}
                  </span>
                )}
              </dd>
            </div>
          </div>
        </dl>

        {data.summary && <p className="mt-5 text-lg text-suave">{data.summary}</p>}
      </header>

      {/* Bloco de inscricao: a acao principal da pagina. */}
      <section className="mt-8" aria-labelledby="titulo-inscricao">
        <Card className={cn(podeInscrever && 'border-borda-forte')}>
          <CardHeader>
            <h2 id="titulo-inscricao" className="flex items-center gap-2 font-serif text-xl font-semibold">
              <Ticket className="size-5" aria-hidden="true" />
              Inscricao
            </h2>
          </CardHeader>
          <CardBody className="space-y-3">
            {data.priceInfo && (
              <p>
                <strong>Valores:</strong> {data.priceInfo}
              </p>
            )}

            {/* RF03.1 exige aviso explicito de que nao ha pagamento online. */}
            {data.registrationsEnabled && (
              <Alert tone="warning">
                <strong>O pagamento e feito pessoalmente.</strong> Este site nao recebe pagamento
                online. Apos se inscrever, procure a secretaria paroquial ou a equipe do evento.
              </Alert>
            )}

            {situacao?.lotado && situacao.aberta && (
              <Alert tone="info">
                As vagas terminaram. Sua inscricao entra na <strong>lista de espera</strong> e a
                paroquia entra em contato se houver desistencia.
              </Alert>
            )}

            {!situacao?.aberta && (
              <p className="font-medium text-suave">{situacao?.motivo}</p>
            )}

            {data.vagasRestantes !== null && data.vagasRestantes !== undefined && !situacao?.lotado && (
              <p className="text-sm text-suave">
                {data.vagasRestantes} {data.vagasRestantes === 1 ? 'vaga restante' : 'vagas restantes'}
                {data.capacity ? ` de ${data.capacity}` : ''}.
              </p>
            )}

            {podeInscrever && (
              <Button asChild size="lg">
                <Link to={`/eventos/${data.slug}/inscricao`}>
                  {situacao?.lotado ? 'Entrar na lista de espera' : 'Inscreva-se'}
                </Link>
              </Button>
            )}

            {inscricaoIndisponivelSemMotivo && (
              <Alert tone="danger">
                <strong>A inscricao online esta temporariamente indisponivel.</strong> Procure a
                secretaria paroquial para se inscrever neste evento.
              </Alert>
            )}

            {!data.registrationsEnabled && (
              <p className="text-sm text-suave">
                A inscricao deste evento e feita pessoalmente, na secretaria paroquial.
              </p>
            )}
          </CardBody>
        </Card>
      </section>

      {data.description && (
        <section className="mt-10">
          <h2 className="font-serif text-2xl font-semibold">Sobre o evento</h2>
          {/* Sanitizado no servidor, na gravacao. Ver NoticiaDetalhePage. */}
          <div
            className="conteudo-rico mt-3"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        </section>
      )}

      <GaleriaDeFotos fotos={data.galeria} titulo="Fotos do evento" />

      <VitrineDePatrocinadores patrocinadores={data.patrocinadores} />
      <BalancetePublicoBloco balancete={data.balancete} />
    </div>
  )
}

/** Iniciais para o lugar da logo quando o patrocinador nao tem imagem. */
function iniciais(nome: string): string {
  // Ignora conectivos: "Padaria e Confeitaria Sao Jose" vira PC, nao PE.
  const palavras = nome
    .split(/\s+/)
    .filter((palavra) => palavra.length > 2 || /^[A-Z]/.test(palavra))
  return palavras
    .slice(0, 2)
    .map((palavra) => palavra[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * RF03.3 - vitrine de patrocinadores, agrupada por cota.
 *
 * Cada apoiador aparece como cartao com logo E nome. Antes era um ou outro - a
 * logo quando existia, o nome apenas como reserva -, e logo de comercio de
 * cidade pequena com frequencia nao traz o nome legivel dentro da imagem: quem
 * lia a pagina via o desenho e nao sabia de quem era.
 *
 * A cota continua mudando o tamanho do cartao e da logo, porque isso e a
 * contrapartida acordada com quem patrocina.
 */
function VitrineDePatrocinadores({
  patrocinadores,
}: {
  patrocinadores: EventDetail['patrocinadores']
}) {
  if (patrocinadores.length === 0) return null

  const porCota = SPONSOR_TIER_ORDER.map((tier) => ({
    tier,
    label: patrocinadores.find((p) => p.tier === tier)?.tierLabel ?? tier,
    itens: patrocinadores.filter((p) => p.tier === tier),
  })).filter((grupo) => grupo.itens.length > 0)

  /**
   * Caixa da logo, a esquerda do nome. Cota mais alta, caixa maior.
   *
   * Largura E altura fixas: com a logo ao lado do texto, uma caixa so com altura
   * deixaria cada cartao com um recuo diferente do nome, conforme a logo fosse
   * quadrada ou deitada - e a coluna de nomes sairia desalinhada na vertical.
   *
   * Classes completas e literais de proposito: o Tailwind lê o codigo-fonte para
   * gerar o CSS, e classe montada por concatenacao nao chega ao build.
   */
  const caixaDaLogo: Record<SponsorTier, string> = {
    DIAMANTE: 'h-20 w-28',
    OURO: 'h-16 w-24',
    PRATA: 'h-14 w-20',
    BRONZE: 'h-12 w-18',
    APOIO: 'h-10 w-16',
  }

  /**
   * Menos colunas que no layout vertical - o cartao agora e deitado.
   *
   * A pagina do evento e `max-w-4xl` (896px). Com logo ao lado do nome, tres
   * colunas dao ~277px por cartao: descontado `p-4`, a caixa da logo e o `gap-3`,
   * restam ~137px para o texto. Em celular fica uma coluna: dois cartoes deitados
   * lado a lado em 400px nao caberiam sem quebrar o nome letra a letra.
   */
  const colunas: Record<SponsorTier, string> = {
    DIAMANTE: 'grid-cols-1 sm:grid-cols-2',
    OURO: 'grid-cols-1 sm:grid-cols-2',
    PRATA: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    BRONZE: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    APOIO: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  }

  const tamanhoDoNome: Record<SponsorTier, string> = {
    DIAMANTE: 'text-base',
    OURO: 'text-base',
    PRATA: 'text-sm',
    BRONZE: 'text-sm',
    APOIO: 'text-sm',
  }

  return (
    <section className="mt-12">
      <h2 className="font-serif text-2xl font-semibold">Quem apoia este evento</h2>
      <p className="mt-1 text-sm text-suave">
        A paroquia agradece aos patrocinadores e apoiadores.
      </p>

      <div className="mt-5 space-y-8">
        {porCota.map((grupo) => (
          <div key={grupo.tier}>
            <h3 className="text-sm font-semibold tracking-wide text-suave uppercase">
              {grupo.label}
            </h3>

            <ul className={cn('mt-3 grid gap-4', colunas[grupo.tier])}>
              {grupo.itens.map((patrocinador) => {
                const nome = patrocinador.shortName || patrocinador.name

                const cartao = (
                  <>
                    {/* shrink-0: sem isso o flex comprime a caixa da logo para
                        acomodar um nome longo, e a logo encolhe junto. */}
                    <div
                      className={cn(
                        'flex shrink-0 items-center justify-center',
                        caixaDaLogo[grupo.tier],
                      )}
                    >
                      {patrocinador.logo ? (
                        <img
                          src={patrocinador.logo.medium}
                          /*
                           * Vazio de proposito: o nome esta escrito ao lado, e
                           * repeti-lo aqui faria o leitor de tela anunciar o
                           * mesmo patrocinador duas vezes.
                           */
                          alt=""
                          loading="lazy"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="flex size-10 items-center justify-center rounded-full bg-primario-suave font-serif text-sm font-semibold text-primario"
                        >
                          {iniciais(patrocinador.name)}
                        </span>
                      )}
                    </div>

                    <p className={cn('font-medium break-words', tamanhoDoNome[grupo.tier])}>
                      {nome}
                    </p>
                  </>
                )

                const classesDoCartao =
                  'flex h-full items-center gap-3 rounded-lg border border-borda bg-cartao p-4'

                return (
                  <li key={`${grupo.tier}-${patrocinador.name}`}>
                    {patrocinador.website ? (
                      <a
                        href={patrocinador.website}
                        target="_blank"
                        // nofollow: a vitrine agradece o apoio, nao vende
                        // autoridade de busca.
                        rel="noopener noreferrer nofollow"
                        title={patrocinador.name}
                        className={cn(
                          classesDoCartao,
                          'transition-colors hover:border-primario hover:bg-afundado',
                        )}
                      >
                        {cartao}
                      </a>
                    ) : (
                      <div className={classesDoCartao} title={patrocinador.name}>
                        {cartao}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}

/** RF03.2 - balancete sintetico publicado, quando o evento autoriza. */
function BalancetePublicoBloco({ balancete }: { balancete: EventDetail['balancete'] }) {
  if (!balancete) return null

  const positivo = balancete.resultadoCents >= 0

  return (
    <section className="mt-12">
      <h2 className="font-serif text-2xl font-semibold">Prestacao de contas</h2>
      <p className="mt-1 text-sm text-suave">
        Resumo das receitas e despesas deste evento, publicado pela paroquia.
      </p>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-md text-sm">
          <caption className="sr-only">Balancete sintetico do evento</caption>
          <tbody>
            {balancete.receitas.length > 0 && (
              <>
                <tr className="border-b border-borda">
                  <th scope="col" colSpan={2} className="py-2 text-left font-semibold text-sucesso">
                    Receitas
                  </th>
                </tr>
                {balancete.receitas.map((linha) => (
                  <tr key={linha.categoria} className="border-b border-borda">
                    <td className="py-2">{linha.label}</td>
                    <td className="py-2 text-right tabular-nums">{formatBRL(linha.totalCents)}</td>
                  </tr>
                ))}
                <tr className="border-b-2 border-borda font-semibold">
                  <td className="py-2">Total de receitas</td>
                  <td className="py-2 text-right tabular-nums text-sucesso">
                    {formatBRL(balancete.totalReceitasCents)}
                  </td>
                </tr>
              </>
            )}

            {balancete.despesas.length > 0 && (
              <>
                <tr className="border-b border-borda">
                  <th scope="col" colSpan={2} className="py-2 pt-5 text-left font-semibold text-perigo">
                    Despesas
                  </th>
                </tr>
                {balancete.despesas.map((linha) => (
                  <tr key={linha.categoria} className="border-b border-borda">
                    <td className="py-2">{linha.label}</td>
                    <td className="py-2 text-right tabular-nums">{formatBRL(linha.totalCents)}</td>
                  </tr>
                ))}
                <tr className="border-b-2 border-borda font-semibold">
                  <td className="py-2">Total de despesas</td>
                  <td className="py-2 text-right tabular-nums text-perigo">
                    {formatBRL(balancete.totalDespesasCents)}
                  </td>
                </tr>
              </>
            )}

            <tr className="text-lg font-bold">
              <td className="py-3">{positivo ? 'Resultado (sobra)' : 'Resultado (deficit)'}</td>
              <td
                className={cn(
                  'py-3 text-right tabular-nums',
                  positivo ? 'text-sucesso' : 'text-perigo',
                )}
              >
                {formatBRL(Math.abs(balancete.resultadoCents))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}
